import React, { useState } from 'react';
import { ListBase, ListProps } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { isLegacyPin, useLinkedPin } from '../Common/DataList';
import ChangeInstancesListPage from './List/ChangeInstancesListPage';
import ConsumerChangeDetail from './List/ConsumerChangeDetail';
import { ChangeInstancesListConfig } from './List/types';

const config: ChangeInstancesListConfig = {
    resource: 'change_instances',
    serviceItemPath: '/service_items',
    applicationsResource: 'applications',
    serviceItemsResource: 'service_items',
    servicesResource: 'consumer_services',
    partyField: {
        label: 'Service Owner',
        team: (record) => record.service_owner_team ?? record.owner ?? record.service?.owner,
        pov: 'serviceowner',
    },
    ExpandedRow: ConsumerChangeDetail,
};

const ChangeList = (props: ListProps) => {
    // ?id= (filters the list down to that change) and ?submission_id= go
    // through the permanent filter, so they reach the API without showing up
    // as GUI filter controls — the list's own schema does not own them, and
    // anything it does not own is pruned from the filter values on mount. Read
    // once: react-admin rewrites the search string when list params change,
    // and these must survive that. While either is active the default state
    // filter is dropped, so a REJECTED/ERROR change a submission links to stays
    // visible. An old `?id=~123` is a pin, not a filter — useLinkedPin has it.
    const location = useLocation();
    const [scoped] = useState(() => {
        const params = new URLSearchParams(location.search);
        return {
            id: isLegacyPin(params.get('id')) ? null : params.get('id'),
            submission_id: params.get('submission_id'),
            application_id: params.get('application_id'),
        };
    });

    // a link to a change (#123) puts it first, whatever page it would be on
    // and whatever the default state filter would make of it
    const pin = useLinkedPin();

    const permanent: Record<string, any> = { light: true, ...pin };
    if (scoped.id) permanent.id = scoped.id;
    if (scoped.submission_id) permanent.submission_id = scoped.submission_id;

    const narrowed = !!(scoped.id || scoped.submission_id);

    /**
     * ?application_id= comes from an application's ring, and unlike the two
     * above it names a filter this list already owns — so it opens the list as
     * a filter *value*, visible on its column and clearable, rather than as a
     * permanent filter with no control behind it.
     *
     * The ring counts what is outstanding, so the list opens on the same four
     * states. The usual default hides errors and rejections, which is the
     * opposite of what someone who just clicked a red slice came to see.
     */
    const filterDefaults = scoped.application_id
        ? { application_id: scoped.application_id, state: 'PENDING,APPROVED,REJECTED,ERROR' }
        : (narrowed ? undefined : { state: 'PENDING,APPROVED' });

    return (
        <ListBase
            // a new link is a new list — see useLinkedPin
            key={pin.pin ?? ''}
            sort={{ field: 'id', order: 'DESC' }}
            filter={permanent}
            perPage={25}
            filterDefaultValues={filterDefaults}
            {...props}
        >
            <ChangeInstancesListPage config={config} />
        </ListBase>
    );
};

export default ChangeList;
